import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import api, { apiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { fmtBoardDims } from '@/lib/units';
import {
  Wand2, Wind, Waves, Thermometer, Loader2, Sparkles, ArrowRight, Ruler,
} from 'lucide-react';
import { toast } from 'sonner';

// Derive a sensible riding style + water state from the spot's character so the
// inline recommendation matches how people actually ride this spot.
const STYLE_BY_TYPE = { wave: 'wave', flat: 'freeride', freestyle: 'freestyle', chop: 'freeride' };
const WATER_BY_TYPE = { wave: 'waves', flat: 'flat', freestyle: 'flat', chop: 'choppy' };

const MiniBlock = ({ icon: Icon, label, accent, children, testid }) => (
  <div className="rounded-xl bg-[#0A0A0A] border border-white/10 p-3.5" data-testid={testid}>
    <div className="flex items-center gap-1.5 text-muted-foreground">
      <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
      <span className="label-eyebrow">{label}</span>
    </div>
    {children}
  </div>
);

export const SpotGearAdvice = ({ spot, nowKn, windDir, units, user }) => {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const fetchAdvice = async () => {
    if (nowKn == null) return;
    setLoading(true);
    setResult(null);
    const payload = {
      spot_id: spot.id,
      location: `${spot.name}, ${spot.country}`,
      wind_knots: nowKn,
      wind_direction: windDir || null,
      weight_kg: user?.weight_kg ?? null,
      skill_level: user?.skill_level || spot.level || 'intermediate',
      riding_style: STYLE_BY_TYPE[spot.type] || 'freeride',
      water_state: WATER_BY_TYPE[spot.type] || 'choppy',
      water_temp_c: null,
      lang: (i18n.resolvedLanguage || i18n.language || 'nl').slice(0, 2),
    };

    const MAX_TRIES = 3;
    let lastErr = null;
    for (let attempt = 1; attempt <= MAX_TRIES; attempt += 1) {
      try {
        const { data } = await api.post('/gear/advice', payload, { timeout: 45000 });
        setResult(data.advice);
        lastErr = null;
        break;
      } catch (err) {
        lastErr = err;
        const status = err?.response?.status;
        const transient = !err.response || status === 429 || (status >= 500 && status < 600);
        if (attempt < MAX_TRIES && transient) {
          await new Promise((r) => setTimeout(r, 900 * attempt));
          continue;
        }
        break;
      }
    }
    if (lastErr) toast.error(t('gear.adviceError'));
    setLoading(false);
  };

  const dims = result ? fmtBoardDims(result.board, units) : null;

  return (
    <div className="mt-4 pt-4 border-t border-white/10" data-testid="spot-gear-advice">
      {!result && (
        <Button
          onClick={fetchAdvice}
          disabled={loading || nowKn == null}
          className="w-full h-11 bg-[#00B4D8] hover:bg-[#48CAE4] text-black font-semibold rounded-xl"
          data-testid="spot-gear-btn"
        >
          {loading ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('spotDetail.gearLoading')}</>
          ) : (
            <><Wand2 className="w-4 h-4 mr-2" /> {t('spotDetail.chooseGear')}</>
          )}
        </Button>
      )}

      {result && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          data-testid="spot-gear-result"
        >
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-[#00B4D8]" />
            <span className="label-eyebrow text-[#48CAE4]">{t('spotDetail.gearForNow')}</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Kite */}
            <MiniBlock icon={Wind} label={t('gear.kiteSize')} accent="#00B4D8" testid="spot-gear-kite">
              <div className="font-data text-2xl font-semibold mt-1.5 text-[#00B4D8]">{result.kite?.primary_size_m2}m²</div>
              {result.kite?.range_m2 && <div className="text-[11px] text-muted-foreground mt-0.5">{result.kite.range_m2}</div>}
              {result.kite?.type && <div className="text-[11px] text-white/70 mt-1">{result.kite.type}</div>}
            </MiniBlock>

            {/* Board with explicit length x width */}
            <MiniBlock icon={Waves} label={t('gear.board')} accent="#48CAE4" testid="spot-gear-board">
              <div className="font-data text-base font-semibold mt-1.5 text-[#48CAE4]">{result.board?.type}</div>
              {dims && (
                <div className="flex items-center gap-1 mt-1 text-white/90">
                  <Ruler className="w-3 h-3 text-[#48CAE4]" />
                  <span className="font-data text-xs">{dims}</span>
                </div>
              )}
            </MiniBlock>

            {/* Wetsuit */}
            <MiniBlock icon={Thermometer} label={t('gear.wetsuit')} accent="#FFB020" testid="spot-gear-wetsuit">
              <div className="font-data text-2xl font-semibold mt-1.5 text-[#FFB020]">{result.wetsuit?.thickness_mm}</div>
              {result.wetsuit?.type && <div className="text-[11px] text-muted-foreground mt-0.5">{result.wetsuit.type}</div>}
            </MiniBlock>
          </div>

          {result.powered_assessment && (
            <p className="text-xs text-muted-foreground mt-3">{result.powered_assessment}</p>
          )}

          <div className="flex items-center justify-between gap-3 mt-3">
            <button
              onClick={fetchAdvice}
              disabled={loading}
              className="text-xs text-muted-foreground hover:text-white transition-colors inline-flex items-center gap-1"
              data-testid="spot-gear-refresh"
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} {t('spotDetail.gearRecalc')}
            </button>
            <Link
              to="/gear-advisor"
              className="text-xs text-[#48CAE4] hover:text-[#00B4D8] inline-flex items-center gap-1"
              data-testid="spot-gear-refine"
            >
              {t('spotDetail.gearRefine')} <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
        </motion.div>
      )}
    </div>
  );
};
